import {FC, Fragment, SVGAttributes, useMemo} from "react";

type Piece = {
  name: string | number;
  value: number;
  colors: [string, string];
};

interface Props extends Omit<SVGAttributes<SVGSVGElement>, "width" | "height" | "viewBox"> {
  pieces?: Piece[];
}

const emptyPieces = [
  {name: "a", value: 10, colors: ["#EDEBF0", "#DBDBDB"] as [string, string]},
  {name: "b", value: 20, colors: ["#EDEBF0", "#DBDBDB"] as [string, string]},
  {name: "c", value: 15, colors: ["#EDEBF0", "#DBDBDB"] as [string, string]},
  {name: "d", value: 30, colors: ["#EDEBF0", "#DBDBDB"] as [string, string]},
] as Piece[];

const Pie: FC<Props> = ({pieces = emptyPieces, className, children, ...svgProps}) => {
  const pieId = svgProps.id || "pie";
  const size = 200;
  const outerWidth = 20;
  const piecesWidth = 12;
  const radius = (size - outerWidth) / 2;
  const center = size / 2;

  const gap = piecesWidth * 2;

  const circleLength = 2 * Math.PI * radius;
  const availableLength = circleLength - gap * pieces.length;

  const piecesConfig = useMemo(() => {
    const piecesLength = pieces.length;
    const half = Math.floor(piecesLength / 2);

    const totalMaybeZero = pieces.reduce((acc, curr) => acc + curr.value, 0);
    const total = totalMaybeZero || piecesLength;

    const left = pieces.slice(0, half);
    const leftSize = left.reduce((acc, curr) => acc + curr.value, 0);
    const leftMidPoint = !totalMaybeZero ? piecesLength / 4 : leftSize / 2;

    const offsetAngle = (-leftMidPoint / total) * 360;
    let currentAngle = offsetAngle;

    return pieces.map((piece) => {
      const id = `pie-${pieId}-${piece.name}`;
      const percentage = totalMaybeZero ? piece.value / total : 1 / piecesLength;
      const pieceLength = percentage * availableLength;
      const initialRotation = currentAngle;
      const angle = ((pieceLength + gap) / circleLength) * 360;
      currentAngle += angle;

      return {
        id,
        percentage: !totalMaybeZero ? 0 : percentage,
        initialRotation,
        pieceLength,
        colors: piece.colors,
        angle,
      };
    });
  }, [pieces, pieId, availableLength, gap, circleLength]);

  const contentSize = (2 * (radius - outerWidth / 2)) / Math.sqrt(2);
  const contentOffset = (size - contentSize) / 2;

  return (
    <svg
      {...svgProps}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={["!aspect-square shrink-0 transition-all", className].asClass}
    >
      <defs>
        {piecesConfig.map((piece) => {
          const rotation = piece.initialRotation;
          const [startColor, endColor] = piece.colors;
          const gradientId = `${piece.id}_grad`;

          // Compute gradient endpoints in SVG userspace along the arc direction
          const startRad = ((rotation - 90) * Math.PI) / 180;
          const endRad = ((rotation + piece.angle - 90) * Math.PI) / 180;
          const gx1 = center + radius * Math.cos(startRad);
          const gy1 = center + radius * Math.sin(startRad);
          const gx2 = center + radius * Math.cos(endRad);
          const gy2 = center + radius * Math.sin(endRad);

          return (
            <linearGradient
              key={gradientId}
              id={gradientId}
              gradientUnits="userSpaceOnUse"
              x1={gx1}
              y1={gy1}
              x2={gx2}
              y2={gy2}
            >
              <stop offset="0%" stopColor={startColor} />
              <stop offset="100%" stopColor={endColor} />
            </linearGradient>
          );
        })}
      </defs>

      {/* Background track */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        stroke="rgba(248,248,255,1)"
        strokeWidth={outerWidth}
        fill="transparent"
      />

      {piecesConfig.map((piece, index) => {
        const rotation = piece.initialRotation;
        const gradientId = `${piece.id}_grad`;
        const dashArray = [piece.pieceLength, circleLength - piece.pieceLength];

        return (
          <Fragment key={piece.id}>
            <circle
              cx={center}
              cy={center}
              r={radius}
              strokeWidth={piecesWidth}
              stroke={`url(#${gradientId})`}
              fill="transparent"
              strokeLinecap="round"
              strokeDashoffset={0}
              strokeDasharray={dashArray.join(" ")}
              // Use SVG transform attribute — more reliable than CSS transform in Safari
              transform={`rotate(${rotation}, ${center}, ${center})`}
              data-piece={index}
              style={{filter: piece.percentage === 0 ? "grayscale(1)" : undefined}}
            />
          </Fragment>
        );
      })}

      {/* Center content slot via foreignObject — only used for non-gradient HTML content */}
      <foreignObject
        x={contentOffset}
        y={contentOffset}
        className="overflow-visible"
        width={contentSize}
        height={contentSize}
      >
        {children}
      </foreignObject>
    </svg>
  );
};

export default Pie;
